import { PropsWithChildren, useEffect } from "react";
import {
  ClockCircleOutlined,
  LoadingOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { Button, Space } from "antd";
import { Chart } from "./lib/Chart";
import { HeatMap } from "./lib/HeatMap";
import { OverTime } from "./lib/OverTime";
import { TreeMap } from "./lib/TreeMap";
import { Flex } from "src/components/Flex";
import { ContentViews, ContentView } from "./lib/useContentView";
import { Metric } from "./lib/Metric";
import { SearchContext } from "src/search/useSearchContext";
import { UnifiedTableVisualization } from "./lib/UnifiedTableVisualization";

export function ResponseCanvas({
  context,
  onRefuse,
}: {
  context: SearchContext & { view: ContentView; title: string };
  onRefuse?: () => void;
}) {
  const { view, params } = context;

  // Handle table view config updates in useEffect
  useEffect(() => {
    if (
      view.is(ContentViews.pivotTable) ||
      view.is(ContentViews.dataTable) ||
      view.is(ContentViews.nestedTable)
    ) {
      const hasGroups = params.selectors.hasGroupBys(params.state);
      const layout = hasGroups
        ? view.is(ContentViews.pivotTable)
          ? "pivot"
          : view.is(ContentViews.nestedTable)
          ? "nested"
          : "flat"
        : "flat";

      const currentConfig = {
        ...view.config,
        layout,
      };

      if (JSON.stringify(currentConfig) !== JSON.stringify(view.config)) {
        view.setConfig(currentConfig);
      }
    }
  }, [view, params.state]);

  // these all take control of the full width and height
  if (view.is(ContentViews.default))
    return (
      <Flex centered style={{ padding: 80 }}>
        <LoadingOutlined />
      </Flex>
    );
  if (view.is(ContentViews.barChart))
    return <Chart onRefuse={onRefuse} context={context} />;
  if (view.is(ContentViews.metric))
    return <Metric onRefuse={onRefuse} context={context} />;
  if (view.is(ContentViews.heatMap))
    return (
      <HeatMap
        context={context}
        onRefuse={onRefuse}
        help={<UngroupedHelper context={context} />}
      />
    );
  if (view.is(ContentViews.treeMap))
    return (
      <TreeMap
        context={context}
        onRefuse={onRefuse}
        help={<UngroupedHelper context={context} />}
      />
    );

  if (view.is(ContentViews.overTime))
    return (
      <ScrollView>
        <OverTime context={context} onRefuse={onRefuse} />
      </ScrollView>
    );

  // Wrap all table views in UnifiedTableVisualization with preserved layout
  if (
    view.is(ContentViews.pivotTable) ||
    view.is(ContentViews.dataTable) ||
    view.is(ContentViews.nestedTable)
  ) {
    return <UnifiedTableVisualization context={context} onRefuse={onRefuse} />;
  }

  // Handle grouped data views
  if (params.selectors.hasGroupBys(params.state)) {
    return <UnifiedTableVisualization context={context} onRefuse={onRefuse} />;
  }

  // Default to EntitiesTable wrapped in UnifiedTableVisualization
  return <UnifiedTableVisualization context={context} onRefuse={onRefuse} />;
}

function ScrollView({ children }: PropsWithChildren) {
  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        overflow: "auto",
      }}
      className="thin_scrollbar"
    >
      {children}
    </div>
  );
}

export const UngroupedHelper = (props: {
  context: SearchContext & { view: ContentView };
}) => {
  const { view } = props.context;
  return (
    <Flex
      centered
      direction="column"
      style={{
        fontSize: 12,
        marginTop: 10,
        marginBottom: 10,
      }}
    >
      You can also view this data as:
      <Space.Compact>
        <Button
          size="small"
          onClick={() => view.set(ContentViews.unifiedTable)}
          type="primary"
          icon={<TableOutlined />}
        >
          A table of data
        </Button>
        <Button
          size="small"
          onClick={() => view.set(ContentViews.overTime)}
          type="primary"
          icon={<ClockCircleOutlined />}
        >
          Graphs over time
        </Button>
      </Space.Compact>
    </Flex>
  );
};
